/**
 * Widget-local hook: encapsulates RasterStatistics actions (factsheet fetching + PDF generation).
 *
 * Constraint: this is a container helper. It should be dependency-injected and must not
 * import global stores directly. UI components remain dumb and receive callbacks.
 */

import { useCallback } from "react";

import { Canvg } from "canvg";

import createFactSheetPdf from "../../../utils/createFactSheetPdf";
import pdfHeader from "../../pdfHeader";
import {
  fetchBackendStatistics,
  getCurrentFilterValues,
  transformBackendResults,
} from "utils/statistic";

/**
 * @param {object} params
 * @param {object} params.CommunicationStore
 * @param {number} params.decimalPlaces
 * @param {object} params.nls
 * @param {string} params.label
 * @param {string} params.entityDisplayText
 * @param {string} params.selectedEntity
 * @param {string} params.infoText
 * @param {any[]} params.mainTableResult
 * @param {any[]} params.aesTableResult
 * @param {object} params.timeOverviewNls
 * @param {object|null} params.chart
 * @param {object|null} params.aesChart
 * @param {string|null} params.Zeitabschnitt
 * @param {object|null} params.currentChoice
 * @param {Function} params.setTableResult
 * @param {Function} params.setResultTableLoading
 * @param {Function} params.setTableError
 */

const useRasterStatisticsActions = ({
  CommunicationStore,
  decimalPlaces,
  nls,
  label,
  entityDisplayText,
  selectedEntity,
  infoText,
  mainTableResult,
  aesTableResult,
  timeOverviewNls,
  chart,
  aesChart,
  Zeitabschnitt,
  currentChoice,
  setTableResult,
  setResultTableLoading,
  setTableError,
}) => {
  const createPDF = useCallback(() => {
    const mainChartPromise =
      chart && typeof chart.toPng === "function"
        ? chart.toPng(Canvg)
        : Promise.resolve({ url: null });

    const aesChartPromise =
      aesChart && typeof aesChart.toPng === "function"
        ? aesChart.toPng(Canvg)
        : Promise.resolve({ url: null });

    Promise.all([mainChartPromise, aesChartPromise])
      .then(([{ url: mainUrl }, { url: aesUrl }]) => {
        createFactSheetPdf({
          label,
          entityDisplayText,
          selectedEntity,
          infoText,
          nls,
          mainTableResult,
          aesTableResult,
          timeOverviewNls,
          chartImageUrl: mainUrl,
          chartDimensions: { width: chart?.width, height: chart?.height },
          aesChartImageUrl: aesUrl,
          aesChartDimensions: {
            width: aesChart?.width,
            height: aesChart?.height,
          },
          pdfHeader,
          Zeitabschnitt,
          currentChoice,
        }).catch((e) => console.error("Error creating PDF", e));
      })
      .catch((e) => console.error("Error creating chart image", e));
  }, [
    aesChart,
    aesTableResult,
    chart,
    currentChoice,
    entityDisplayText,
    infoText,
    label,
    mainTableResult,
    nls,
    selectedEntity,
    timeOverviewNls,
    Zeitabschnitt,
  ]);

  const createFactSheet = useCallback(() => {
    setTableResult(null);

    const filterValues = getCurrentFilterValues(CommunicationStore);
    const selectionGraphic = CommunicationStore.getMessagesToID({
      to: "selectionGraphic",
    })?.message;

    const objectId = selectionGraphic?.entity?.ObjectId;
    const serviceId = selectionGraphic?.entity?.GisServiceId;

    if (
      filterValues.Kategorie &&
      filterValues.Parameter &&
      objectId &&
      serviceId
    ) {
      setResultTableLoading(true);
      setTableError(false);

      fetchBackendStatistics({
        CategoryId: filterValues.Kategorie,
        ParameterId: filterValues.Parameter,
        SeasonId: filterValues.Zeitabschnitt || null,
        ObjectId: objectId,
        ServiceId: serviceId,
      })
        .then((backendResults) => {
          const transformedResults = transformBackendResults(
            backendResults,
            decimalPlaces,
            selectionGraphic?.graphic.geometry
          );
          setTableResult(transformedResults);
          setResultTableLoading(false);
        })
        .catch((e) => {
          console.error("An error occurred while fetching statistics", e);
          setResultTableLoading(false);
          setTableError(true);
        });
    } else {
      console.warn(
        "Cannot create factsheet: missing required backend parameters.",
        {
          filterValues,
          objectId,
          serviceId,
        }
      );
      setTableError(true);
    }
  }, [
    CommunicationStore,
    decimalPlaces,
    setResultTableLoading,
    setTableError,
    setTableResult,
  ]);

  return {
    createFactSheet,
    createPDF,
  };
};

export default useRasterStatisticsActions;
